import apiClient from '../../../services/apiClient';
import type { ClinicalSummary } from '../../../services/patientService';

interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
    last_page?: number;
}

export const patientService = {
  async getAll(params?: { page?: number; limit?: number; search?: string; per_page?: number }): Promise<{ data: any[]; total: number; current_page: number; last_page: number }> {
    const queryParams = new URLSearchParams();
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await apiClient.get<PaginatedResponse<any>>(`/patients?${queryParams.toString()}`);
    const raw = response.data;
    return {
        data: raw.data || [],
        total: raw.total || 0,
        current_page: raw.current_page || 1,
        last_page: raw.last_page || 1,
    };
  },

  async search(term: string): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(`/patients/search?term=${encodeURIComponent(term)}`);
    return response.data.data || [];
  },

  async getById(id: number): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(`/patients/${id}`);
    return response.data.data;
  },

  async getWithHistory(id: number): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(`/patients/${id}/history`);
    return response.data.data;
  },

  // Types come from src/services/patientService, which is the fuller of the
  // two patient services in this codebase. This one is what PatientDetailPage
  // imports, so the method lives here too until they are merged.
  async getClinicalSummary(id: number): Promise<ClinicalSummary> {
    const response = await apiClient.get<ApiResponse<ClinicalSummary>>(`/patients/${id}/summary`);
    return response.data.data;
  },

  async createOrUpdate(patient: any, id?: number): Promise<any> {
    if (id) {
      const response = await apiClient.put<ApiResponse<any>>(`/patients/${id}`, patient);
      return response.data.data;
    }
    const response = await apiClient.post<ApiResponse<any>>('/patients', patient);
    return response.data.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/patients/${id}`);
  },

  // Allergies
  async getAllergies(patientId: number): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(`/patients/${patientId}/allergies`);
    return response.data.data || [];
  },

  async addAllergy(patientId: number, allergyId: number, notes?: string): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(`/patients/${patientId}/allergies`, {
      allergy_id: allergyId,
      notes,
    });
    return response.data.data;
  },

  async removeAllergy(patientId: number, allergyId: number): Promise<void> {
    await apiClient.delete(`/patients/${patientId}/allergies/${allergyId}`);
  },

  // Contacts
  async getContacts(patientId: number): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(`/patients/${patientId}/contacts`);
    return response.data.data || [];
  },

  async addContact(patientId: number, contact: any): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(`/patients/${patientId}/contacts`, contact);
    return response.data.data;
  },

  async updateContact(patientId: number, contactId: number, contact: any): Promise<any> {
    const response = await apiClient.put<ApiResponse<any>>(`/patients/${patientId}/contacts/${contactId}`, contact);
    return response.data.data;
  },

  async deleteContact(patientId: number, contactId: number): Promise<void> {
    await apiClient.delete(`/patients/${patientId}/contacts/${contactId}`);
  },

  // Insurances
  async getInsurances(patientId: number): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(`/patients/${patientId}/insurances`);
    return response.data.data || [];
  },

  async addInsurance(patientId: number, insurance: any): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(`/patients/${patientId}/insurances`, insurance);
    return response.data.data;
  },

  async updateInsurance(patientId: number, insuranceId: number, insurance: any): Promise<any> {
    const response = await apiClient.put<ApiResponse<any>>(`/patients/${patientId}/insurances/${insuranceId}`, insurance);
    return response.data.data;
  },

  async deleteInsurance(patientId: number, insuranceId: number): Promise<void> {
    await apiClient.delete(`/patients/${patientId}/insurances/${insuranceId}`);
  },
};
